import { Button, cn } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import type { ReactNode } from 'react';

type HomeStageBannerProps = Readonly<{
  namespace: string;
  href: string;
  artworkSrc: string;
  eyebrow?: ReactNode;
  headingId?: string;
  className?: string;
}>;

function HomeStageBanner({ namespace, href, artworkSrc, eyebrow, headingId = 'home-stage-banner-heading', className }: HomeStageBannerProps) {
  const t = useTranslations(namespace);
  const titleLine1 = t('title_line1');
  const titleLine2 = t('title_line2');

  return (
    <section
      aria-labelledby={headingId}
      data-testid="home-stage-banner"
      data-track-container="Stage"
      className={cn(
        'flex flex-col overflow-hidden rounded-2xl border-2 border-border-active bg-accent-green shadow-[0_3px_0_0_var(--color-foreground)] sm-md:flex-row sm-md:items-stretch',
        className
      )}
    >
      <div
        className="flex min-w-0 flex-1 flex-col items-start gap-3 px-5 py-6 text-foreground-thedailymillion sm-md:self-center sm-md:px-8"
        data-track-container="Lottery"
        data-track-product={titleLine1}
        data-track-value={titleLine2}
      >
        <div className="paragraph-s" data-testid="home-stage-banner-eyebrow">
          {eyebrow ?? t('eyebrow')}
        </div>
        <h2 id={headingId} className="flex flex-col">
          <span className="display-xs-strong md:display-s-strong">{titleLine1}</span>
          <span className="display-s-strong md:heading-3">{titleLine2}</span>
        </h2>
        <Button asChild variant="primary" size="m" data-track-name="Play">
          <Link href={href} data-testid="home-stage-banner-cta">
            {t('cta')}
          </Link>
        </Button>
      </div>
      <div className="relative aspect-[32/19] w-full sm-md:w-auto sm-md:max-w-[45%] sm-md:shrink-0 sm-md:self-stretch">
        <Image
          src={artworkSrc}
          alt=""
          aria-hidden
          draggable={false}
          fill
          sizes="(min-width: 640px) 45vw, 100vw"
          className="pointer-events-none object-contain object-bottom sm-md:object-right-bottom"
        />
      </div>
    </section>
  );
}

export { HomeStageBanner };
export type { HomeStageBannerProps };
